/**
 * Pause payload contracts.
 *
 * Every pause flows through the same `paused` state, distinguished by `reason`.
 * Each reason has a defined `on_pause` payload (what the system records when
 * entering the pause) and an `on_resolve` payload (what the human must provide
 * to resume).
 *
 * These contracts are shared between the API and the web UI so that adding a
 * new pause reason is impossible without defining its contract.
 */
import { z } from 'zod';
import type { WorkState } from './states.js';

export const PAUSE_REASONS = ['approval', 'review', 'clarification', 'guardrail'] as const;
export type PauseReason = (typeof PAUSE_REASONS)[number];

// ---------------------------------------------------------------------------
// approval — pause after `plan` completes if approval_required is true
// ---------------------------------------------------------------------------

export const ApprovalOnPauseSchema = z.object({
  plan_id: z.string().uuid(),
  proposed_plan: z.string(),
  context_summary: z.string().optional(),
});
export type ApprovalOnPause = z.infer<typeof ApprovalOnPauseSchema>;

export const ApprovalOnResolveSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve'), notes: z.string().optional() }),
  z.object({
    decision: z.literal('edit'),
    /** The complete new plan markdown — replaces the existing plan verbatim. */
    edited_plan: z.string().min(1),
    notes: z.string().optional(),
  }),
  z.object({
    decision: z.literal('request_changes'),
    /** A narrative description of changes to apply. The service merges these into the current plan via an AI call before transitioning to execute. */
    requested_changes: z.string().min(1),
    notes: z.string().optional(),
  }),
  z.object({ decision: z.literal('reject'), notes: z.string().optional() }),
]);
export type ApprovalOnResolve = z.infer<typeof ApprovalOnResolveSchema>;

// ---------------------------------------------------------------------------
// review — pause after `execute` completes if review_required is true
// ---------------------------------------------------------------------------

export const ReviewOnPauseSchema = z.object({
  output_summary: z.string(),
  artifact_refs: z.array(z.string().uuid()).default([]),
  diff_refs: z.array(z.string().uuid()).default([]),
});
export type ReviewOnPause = z.infer<typeof ReviewOnPauseSchema>;

export const ReviewOnResolveSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('accept'), notes: z.string().optional() }),
  z.object({
    decision: z.literal('request_changes'),
    feedback: z.string().min(1),
  }),
]);
export type ReviewOnResolve = z.infer<typeof ReviewOnResolveSchema>;

// ---------------------------------------------------------------------------
// clarification — agent-initiated; resumes to the same state
// ---------------------------------------------------------------------------

export const ClarificationOnPauseSchema = z.object({
  question: z.string().min(1),
  /**
   * Why the agent is asking — what specific gap or ambiguity needs closing.
   * Surfaced in the UI alongside the question. Optional for compatibility,
   * but the triage prompt requires the model to provide it.
   */
  reason: z.string().optional(),
  context: z.string().optional(),
  current_state: z.enum(['triage', 'plan', 'execute']) satisfies z.ZodType<WorkState>,
});
export type ClarificationOnPause = z.infer<typeof ClarificationOnPauseSchema>;

export const ClarificationOnResolveSchema = z.object({
  answer: z.string().min(1),
});
export type ClarificationOnResolve = z.infer<typeof ClarificationOnResolveSchema>;

// ---------------------------------------------------------------------------
// guardrail — rule-driven; resumes to execute (typically)
// ---------------------------------------------------------------------------

export const GuardrailOnPauseSchema = z.object({
  guardrail_id: z.string(),
  proposed_action: z.object({
    tool: z.string(),
    parameters: z.record(z.unknown()),
  }),
  risk_summary: z.string(),
  allowed_responses: z.array(z.enum(['approve', 'deny', 'modify'])).default([
    'approve',
    'deny',
    'modify',
  ]),
});
export type GuardrailOnPause = z.infer<typeof GuardrailOnPauseSchema>;

export const GuardrailOnResolveSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve'), notes: z.string().optional() }),
  z.object({ decision: z.literal('deny'), notes: z.string().optional() }),
  z.object({
    decision: z.literal('modify'),
    modified_parameters: z.record(z.unknown()),
    notes: z.string().optional(),
  }),
]);
export type GuardrailOnResolve = z.infer<typeof GuardrailOnResolveSchema>;

// ---------------------------------------------------------------------------
// Discriminated union for any pause
// ---------------------------------------------------------------------------

export const PauseOnPauseSchema = z.discriminatedUnion('reason', [
  z.object({ reason: z.literal('approval'), payload: ApprovalOnPauseSchema }),
  z.object({ reason: z.literal('review'), payload: ReviewOnPauseSchema }),
  z.object({ reason: z.literal('clarification'), payload: ClarificationOnPauseSchema }),
  z.object({ reason: z.literal('guardrail'), payload: GuardrailOnPauseSchema }),
]);
export type PauseOnPause = z.infer<typeof PauseOnPauseSchema>;

export const PauseOnResolveSchema = z.discriminatedUnion('reason', [
  z.object({ reason: z.literal('approval'), resolution: ApprovalOnResolveSchema }),
  z.object({ reason: z.literal('review'), resolution: ReviewOnResolveSchema }),
  z.object({ reason: z.literal('clarification'), resolution: ClarificationOnResolveSchema }),
  z.object({ reason: z.literal('guardrail'), resolution: GuardrailOnResolveSchema }),
]);
export type PauseOnResolve = z.infer<typeof PauseOnResolveSchema>;
